import React, { useMemo, useState, useCallback, useRef } from 'react';
import { 
  StackedBarChartProps, 
  ThemeConfig, 
  PresetTheme,
  BarClickData,
  ChartDimensions,
} from '../types';
import { ChartContainer, Axis, Bar, Legend, Tooltip, Title } from './core';
import { 
  calculateScale, 
  getNumericValue, 
  valueToPosition,
} from '../utils/scales';
import { 
  getActiveTheme, 
  mergeAxisConfig, 
  mergeLegendConfig, 
  mergeTooltipConfig, 
  mergeAnimationConfig,
} from '../utils/defaults';
import { getDatasetColor, getHoverColor, resolveColor } from '../utils/colors';
import { getStackedBorderRadius } from '../utils/svg';

export interface StackedBarChartComponentProps extends StackedBarChartProps {
  /** Preset theme name */
  theme?: PresetTheme;
  /** Custom theme configuration */
  themeConfig?: ThemeConfig;
}

/**
 * StackedBarChart Component
 * 
 * A stacked bar chart that shows the composition of data across categories.
 * All datasets are stacked on top of each other.
 * Fully responsive with mobile-optimized rendering.
 * 
 * @example
 * ```tsx
 * <StackedBarChart
 *   labels={['2020', '2021', '2022']}
 *   datasets={[
 *     { label: 'Product A', data: [100, 150, 200] },
 *     { label: 'Product B', data: [80, 120, 160] },
 *     { label: 'Product C', data: [60, 90, 140] }
 *   ]}
 *   theme="vibrant"
 * />
 * ```
 */
export const StackedBarChart: React.FC<StackedBarChartComponentProps> = ({
  labels,
  datasets,
  title,
  xAxis,
  yAxis,
  legend,
  tooltip,
  animation,
  width,
  height = 300,
  padding,
  backgroundColor,
  onBarClick,
  onBarHover,
  className,
  style,
  id,
  ariaLabel,
  borderRadius = 0,
  orientation = 'vertical',
  groupGap = 0.3,
  showValues,
  valueLabels,
  theme = 'default',
  themeConfig,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{
    x: number;
    y: number;
    value: number | [number, number];
    label: string;
    datasetLabel: string;
    color: string;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());

  // Get theme
  const activeTheme = useMemo(() => {
    return getActiveTheme(theme, themeConfig);
  }, [theme, themeConfig]);

  const colors = activeTheme.colors || [];

  // Filter visible datasets
  const visibleDatasets = useMemo(() => {
    return datasets.map((ds, i) => ({
      ...ds,
      hidden: ds.hidden || hiddenDatasets.has(i),
    }));
  }, [datasets, hiddenDatasets]);

  // Merge configs
  const xAxisConfig = useMemo(() => mergeAxisConfig(xAxis, undefined, activeTheme), [xAxis, activeTheme]);
  const yAxisConfig = useMemo(() => mergeAxisConfig(yAxis, undefined, activeTheme), [yAxis, activeTheme]);
  const legendConfig = useMemo(() => mergeLegendConfig(legend, undefined, activeTheme), [legend, activeTheme]);
  const tooltipConfig = useMemo(() => mergeTooltipConfig(tooltip, undefined, activeTheme), [tooltip, activeTheme]);
  const animationConfig = useMemo(() => mergeAnimationConfig(animation), [animation]);

  // Calculate scale for stacked data
  const scale = useMemo(() => {
    const activeDatasets = visibleDatasets.filter(ds => !ds.hidden);
    return calculateScale(activeDatasets, {
      min: orientation === 'vertical' ? yAxis?.min : xAxis?.min,
      max: orientation === 'vertical' ? yAxis?.max : xAxis?.max,
      beginAtZero: true,
      maxTicks: (orientation === 'vertical' ? yAxis?.ticks?.maxTicksLimit : xAxis?.ticks?.maxTicksLimit) || 8,
      stacked: true,
    });
  }, [visibleDatasets, xAxis, yAxis, orientation]);

  // Handle bar hover with exact cursor position
  const handleBarHover = useCallback((
    e: React.MouseEvent, 
    data: BarClickData | null,
    mousePos?: { x: number; y: number }
  ) => {
    if (data) {
      setTooltipData({
        x: 0,
        y: 0,
        value: data.value as number,
        label: data.label,
        datasetLabel: data.datasetLabel,
        color: resolveColor(
          datasets[data.datasetIndex].backgroundColor,
          data.dataIndex,
          getNumericValue(data.value),
          getDatasetColor(data.datasetIndex, colors)
        ),
        mouseX: mousePos?.x,
        mouseY: mousePos?.y,
      });
    } else {
      setTooltipData(null);
    }
    onBarHover?.(e, data);
  }, [datasets, colors, onBarHover]);

  const handleToggleDataset = useCallback((index: number) => {
    setHiddenDatasets(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Generate accessible description
  const chartDescription = useMemo(() => {
    const totals = labels.map((label, i) => {
      const total = datasets.reduce((sum, ds) => sum + getNumericValue(ds.data[i]), 0);
      return `${label}: ${total}`;
    }).join(', ');
    return `Stacked bar chart showing ${datasets.length} series. Totals - ${totals}`;
  }, [datasets, labels]);

  const containerWrapperRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState<ChartDimensions | null>(null);

  return (
    <div ref={containerWrapperRef} style={{ position: 'relative' }}>
      <ChartContainer
        width={width}
        height={height}
        padding={padding}
        backgroundColor={backgroundColor || activeTheme.backgroundColor}
        className={className}
        style={style}
        id={id}
        ariaLabel={ariaLabel || chartDescription}
        title={typeof title === 'string' ? title : title?.text}
      >
        {(dimensions, containerSvgRef, isMobile) => {
          (svgRef as any).current = containerSvgRef.current;
          // Store dimensions for tooltip
          if (!chartDimensions || chartDimensions.width !== dimensions.width) {
            setTimeout(() => setChartDimensions(dimensions), 0);
          }
        
        const { chartWidth, chartHeight } = dimensions;
        const isVertical = orientation === 'vertical';
        
        // Responsive gap
        const responsiveGroupGap = isMobile ? Math.max(0.2, groupGap * 0.8) : groupGap;
        
        // Calculate bar dimensions
        const numGroups = labels.length;
        const categorySize = isVertical ? chartWidth / numGroups : chartHeight / numGroups;
        const groupPadding = categorySize * responsiveGroupGap;
        const barSize = categorySize - groupPadding;

        // Calculate stacked positions
        const bars: React.ReactNode[] = [];
        
        labels.forEach((label, dataIndex) => {
          let positiveStack = 0;
          let negativeStack = 0;
          
          // Get visible datasets for this index to determine first/last
          const visibleForIndex = visibleDatasets
            .map((ds, i) => ({ ds, i, value: getNumericValue(ds.data[dataIndex]) }))
            .filter(item => !item.ds.hidden);

          const positiveItems = visibleForIndex.filter(item => item.value >= 0);
          const negativeItems = visibleForIndex.filter(item => item.value < 0);

          visibleDatasets.forEach((dataset, datasetIndex) => {
            if (dataset.hidden) return;

            const value = dataset.data[dataIndex];
            const numValue = getNumericValue(value);
            const isPositive = numValue >= 0;
            
            // Determine if first/last in stack
            const stackItems = isPositive ? positiveItems : negativeItems;
            const stackIndex = stackItems.findIndex(item => item.i === datasetIndex);
            const isFirst = stackIndex === 0;
            const isLast = stackIndex === stackItems.length - 1;

            // Calculate position
            const categoryStart = dataIndex * categorySize + groupPadding / 2;
            
            let x: number, y: number, barWidth: number, barHeight: number;
            
            if (isVertical) {
              x = categoryStart;
              barWidth = barSize;
              
              if (isPositive) {
                const startPos = valueToPosition(positiveStack, scale, chartHeight, true);
                const endPos = valueToPosition(positiveStack + numValue, scale, chartHeight, true);
                y = endPos;
                barHeight = startPos - endPos;
                positiveStack += numValue;
              } else {
                const startPos = valueToPosition(negativeStack, scale, chartHeight, true);
                const endPos = valueToPosition(negativeStack + numValue, scale, chartHeight, true);
                y = startPos;
                barHeight = endPos - startPos;
                negativeStack += numValue;
              }
            } else {
              y = categoryStart;
              barHeight = barSize;
              
              if (isPositive) {
                const startPos = valueToPosition(positiveStack, scale, chartWidth, false);
                const endPos = valueToPosition(positiveStack + numValue, scale, chartWidth, false);
                x = startPos;
                barWidth = endPos - startPos;
                positiveStack += numValue;
              } else {
                const startPos = valueToPosition(negativeStack, scale, chartWidth, false);
                const endPos = valueToPosition(negativeStack + numValue, scale, chartWidth, false);
                x = endPos;
                barWidth = startPos - endPos;
                negativeStack += numValue;
              }
            }

            // Get colors
            const fillColor = resolveColor(
              dataset.backgroundColor,
              dataIndex,
              numValue,
              getDatasetColor(datasetIndex, colors)
            );
            const strokeColor = resolveColor(dataset.borderColor, dataIndex, numValue, fillColor);
            const hoverFillColor = resolveColor(
              dataset.hoverBackgroundColor,
              dataIndex,
              numValue,
              getHoverColor(fillColor)
            );

            // Get stacked border radius (responsive)
            const baseRadius = dataset.borderRadius ?? borderRadius ?? activeTheme.borderRadius ?? 0;
            const radius = isMobile && typeof baseRadius === 'number' 
              ? Math.max(2, baseRadius * 0.6) 
              : baseRadius;
            const stackedRadius = getStackedBorderRadius(radius, isFirst, isLast, orientation, isPositive);

            bars.push(
              <Bar
                key={`${datasetIndex}-${dataIndex}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={dataset.borderWidth || 0}
                borderRadius={stackedRadius}
                opacity={dataset.opacity}
                animation={animationConfig}
                animationDelay={(animationConfig.staggerDelay || 0) * (datasetIndex * labels.length + dataIndex)}
                datasetIndex={datasetIndex}
                dataIndex={dataIndex}
                value={value}
                label={label}
                datasetLabel={dataset.label}
                onClick={onBarClick}
                onHover={handleBarHover}
                hoverFill={hoverFillColor}
                isMobile={isMobile}
              />
            );
          });
        });

        return (
          <>
            <Title 
              config={title} 
              dimensions={dimensions}
              defaultColor={activeTheme.textColor}
              defaultFontFamily={activeTheme.fontFamily}
            />

            <Axis
              type="x"
              config={isVertical ? xAxisConfig : { ...xAxisConfig, ...yAxisConfig }}
              scale={scale}
              dimensions={dimensions}
              labels={isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />
            <Axis
              type="y"
              config={isVertical ? yAxisConfig : xAxisConfig}
              scale={scale}
              dimensions={dimensions}
              labels={!isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />

            <g className="bars" role="list" aria-label="Stacked data bars">
              {bars}
            </g>

            <Legend
              config={legendConfig}
              datasets={visibleDatasets}
              colors={colors}
              dimensions={dimensions}
              onToggleDataset={handleToggleDataset}
              isMobile={isMobile}
            />

          </>
        );
      }}
    </ChartContainer>
      
      {/* Tooltip rendered outside SVG for proper positioning */}
      {tooltipConfig.enabled && tooltipData && chartDimensions && (
        <Tooltip
          config={tooltipConfig}
          data={tooltipData}
          dimensions={chartDimensions}
          containerRef={svgRef}
        />
      )}
    </div>
  );
};

export default StackedBarChart;
